import { Form } from '$components'
import { z } from 'zod'
import { createContext, useContext, ReactNode } from 'react'
import { CreateCoatTypeFormContent } from './content'
import { CreateCoatTypeFormControls } from './controls'

export enum Input {
  Name = 'name',
}

export const createCoatTypeSchema = z.object({
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
})

export type CreateCoatTypeSchema = z.infer<typeof createCoatTypeSchema>

type CreateCoatTypeFormContextType = {
  onSubmit: (values: CreateCoatTypeSchema) => void
  onCancel: () => void
  isLoading?: boolean
}

const CreateCoatTypeFormContext = createContext<CreateCoatTypeFormContextType | undefined>(undefined)

type CreateCoatTypeFormProps = CreateCoatTypeFormContextType & {
  children?: ReactNode
}

export function CreateCoatTypeForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
}: CreateCoatTypeFormProps) {
  const context: CreateCoatTypeFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <CreateCoatTypeFormContext.Provider value={context}>
      <Form<CreateCoatTypeSchema, typeof createCoatTypeSchema>
        schema={createCoatTypeSchema}
        onSubmit={onSubmit}
      >
        {children}
      </Form>
    </CreateCoatTypeFormContext.Provider>
  )
}

export function useCreateCoatTypeForm() {
  const context = useContext(CreateCoatTypeFormContext)
  if (context === undefined) {
    throw new Error('useCreateCoatTypeForm must be used within a CreateCoatTypeForm')
  }
  return context
}

CreateCoatTypeForm.Content = CreateCoatTypeFormContent
CreateCoatTypeForm.Controls = CreateCoatTypeFormControls
